import React, { useState } from 'react'
import { Spin, Typography, Empty } from 'antd'
import { SearchOutlined, ExperimentOutlined, PlusOutlined } from '@ant-design/icons'

import { useCompounds } from '../../hooks/useCompounds'
import { useDispenseUnits } from '../../../../../../hooks/useDispenseUnits'
import { CompoundDetailModal } from './CompoundDetailModal'
import { CompoundFormModal } from './CompoundFormModal'

import { 
    StyledModal, 
    SearchRow, 
    StyledSearch, 
    CountBadge, 
    AddButton, 
    ListContainer, 
    CompoundRow, 
    CompoundName, 
    ChevronIcon, 
    EmptyState 
} from './styles'

const { Text } = Typography

export const CompoundsModal = ({ open, onClose }) => {
    // selectedCompound: detail view; undefined = closed, object = viewing
    const [selectedCompound, setSelectedCompound] = useState(undefined)
    // formCompound: form view; undefined = closed, null = creating, object = editing
    const [formCompound, setFormCompound] = useState(undefined)

    const {
        compounds,
        loading,
        search,
        setSearch,
        handleCreate,
        handleUpdate,
        handleDelete,
    } = useCompounds()

    const {
        dispenseUnits,
        loading: dispenseUnitsLoading,
    } = useDispenseUnits()

    const handleDeleteAndClose = async (compoundId) => {
        await handleDelete(compoundId)
        setSelectedCompound(undefined)
    }

    const handleEditClick = (compound) => {
        setSelectedCompound(undefined)
        setFormCompound(compound)
    }

    const handleFormSubmit = async (values) => {
        if (formCompound) {
            return handleUpdate(formCompound._id, values)
        }
        return handleCreate(values)
    }

    const countLabel = search
        ? `${compounds.length} match${compounds.length !== 1 ? 'es' : ''}`
        : `${compounds.length} compound${compounds.length !== 1 ? 's' : ''}`

    return (
        <>
            <StyledModal
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ExperimentOutlined />
                        Manage Compounds
                    </span>
                }
                open={open}
                onCancel={onClose}
                destroyOnClose
                centered
                width='56vw'
                footer={null}
            >
                <SearchRow>
                    <StyledSearch
                        placeholder="Search by compound name..."
                        prefix={<SearchOutlined style={{ color: '#c0c0c0' }} />}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                    />
                    {!loading && (
                        <CountBadge>{countLabel}</CountBadge>
                    )}
                    <AddButton
                        icon={<PlusOutlined />}
                        onClick={() => setFormCompound(null)}
                    >
                        Add
                    </AddButton>
                </SearchRow>

                <Spin spinning={loading}>
                    {!loading && compounds.length === 0 ? (
                        <EmptyState>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Text style={{ color: '#8c8c8c' }}>
                                        {search ? 'No compounds match your search.' : 'No compounds found.'}
                                    </Text>
                                }
                            />
                        </EmptyState>
                    ) : (
                        <ListContainer>
                            {compounds.map(compound => (
                                <CompoundRow
                                    key={compound._id}
                                    onClick={() => setSelectedCompound(compound)}
                                >
                                    <CompoundName>{compound.name}</CompoundName>
                                    <ChevronIcon />
                                </CompoundRow>
                            ))}
                        </ListContainer>
                    )}
                </Spin>
            </StyledModal>

            {selectedCompound && (
                <CompoundDetailModal
                    compound={selectedCompound}
                    dispenseUnits={dispenseUnits}
                    dispenseUnitsLoading={dispenseUnitsLoading}
                    onClose={() => setSelectedCompound(undefined)}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteAndClose}
                />
            )}

            {formCompound !== undefined && (
                <CompoundFormModal
                    compound={formCompound}
                    dispenseUnits={dispenseUnits}
                    onClose={() => setFormCompound(undefined)}
                    onSubmit={handleFormSubmit}
                />
            )}
        </>
    )
}
